
import React, { useState } from 'react';
import { ChevronDown, Trash2, ToggleLeft, ToggleRight, Copy } from 'lucide-react';
import { format } from 'date-fns';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { SafeApiConnection, TradingAccount, CTraderProfile } from '@/types/trader';
import { AccountStatus } from './trader-card/AccountStatus';
import { EnhancedAccountBalance } from './trader-card/EnhancedAccountBalance';
import { PlatformLogo } from './trader-card/PlatformLogo';
import { useTranslation } from 'react-i18next';
import { AccountTypeBadge } from '@/components/common/AccountTypeBadge';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface TraderAccountCardProps {
  tradingAccount: TradingAccount;
  apiConnection: SafeApiConnection;
  cspProfile?: CTraderProfile;
  onToggleConnection: (apiConnectionId: string, newStatus: boolean) => Promise<void>;
  onDelete: (apiConnectionId: string) => Promise<void>;
}

export function TraderAccountCard({ 
  tradingAccount, 
  apiConnection,
  cspProfile,
  onToggleConnection,
  onDelete
}: TraderAccountCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isConnected = apiConnection.status === 'connected';
  const platformName = cspProfile?.broker || cspProfile?.prop_firm || 'CTrader';
  
  const handleToggleConnection = async () => {
    setIsLoading(true);
    try {
      await onToggleConnection(apiConnection.id, !isConnected);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await onDelete(apiConnection.id);
    } finally {
      setIsLoading(false);
    }
  };

  const copyAccountNumber = () => {
    navigator.clipboard.writeText(tradingAccount.number_account_trading);
    toast({
      title: t('copied_to_clipboard'),
      description: t('account_number_copied')
    });
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="w-full transition-all duration-300 hover:scale-[1.02]"
    >
      <Card className="overflow-hidden border hover:border-primary/30 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-background to-background/50">
        <div className={`${isMobile ? 'p-4' : 'p-5'}`}>
          <CollapsibleTrigger asChild className="w-full">
            <div className="flex justify-between items-start cursor-pointer group">
              <div className="flex items-center gap-3 flex-1">
                <PlatformLogo platformName={platformName} size={isMobile ? 'sm' : 'md'} />
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`${isMobile ? 'text-base' : 'text-lg'} font-bold font-mono tracking-wider text-foreground group-hover:text-primary transition-colors`}>
                      {tradingAccount.number_account_trading}
                    </span>
                    {!isMobile && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyAccountNumber();
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground font-medium truncate`}>
                    {cspProfile?.csp_account_name || t('unknown')}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <AccountStatus isConnected={isConnected} size={isMobile ? 'sm' : 'md'} />
                <ChevronDown 
                  className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} transition-all duration-300 text-muted-foreground group-hover:text-primary ${isOpen ? 'rotate-180' : ''}`} 
                />
              </div>
            </div>
          </CollapsibleTrigger>

          <div className={`flex justify-between items-center ${isMobile ? 'mt-3' : 'mt-4'}`}>
            <EnhancedAccountBalance balance={tradingAccount.balance_account} size={isMobile ? 'sm' : 'md'} />
            <AccountTypeBadge isLive={tradingAccount.live} size={isMobile ? 'sm' : 'md'} />
          </div>

          <CollapsibleContent className="transition-all duration-300 ease-in-out">
            <div className={`${isMobile ? 'mt-4 pt-3' : 'mt-6 pt-4'} border-t border-border/60`}>
              {cspProfile?.created_at && (
                <div className={`${isMobile ? 'mb-3' : 'mb-4'} flex items-center justify-between bg-muted/30 rounded-lg ${isMobile ? 'p-2' : 'p-3'}`}>
                  <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground font-medium`}>
                    {t('added')}:
                  </span>
                  <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-semibold text-foreground`}>
                    {format(new Date(cspProfile.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
              )}
              
              <div className={`flex items-center justify-between ${isMobile ? 'gap-2' : 'gap-3'}`}>
                <button
                  type="button"
                  onClick={handleToggleConnection}
                  className={`flex items-center ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 ${isMobile ? 'text-xs' : 'text-sm'} font-medium gap-2 shadow-sm
                    ${isConnected 
                      ? 'bg-primary/15 border border-primary/30 text-primary hover:bg-primary/20' 
                      : 'bg-muted hover:bg-muted/80 border border-muted-foreground/20 text-muted-foreground hover:text-foreground'}
                  `}
                  disabled={isLoading}
                  aria-pressed={isConnected}
                >
                  {isConnected ? (
                    <>
                      <ToggleRight className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
                      {!isMobile && <span>{t('disconnect')}</span>}
                      {isMobile && <span>Disconnect</span>}
                    </>
                  ) : (
                    <>
                      <ToggleLeft className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
                      {!isMobile && <span>{t('reconnect')}</span>}
                      {isMobile && <span>Reconnect</span>}
                    </>
                  )}
                </button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className={`${isMobile ? 'h-8 w-8' : 'h-10 w-10'} text-destructive hover:bg-destructive/10 hover:text-destructive border border-transparent hover:border-destructive/20 transition-all duration-200`}
                      disabled={isLoading}
                    >
                      <Trash2 className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className={isMobile ? 'max-w-[95vw]' : ''}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('delete_trading_account')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('delete_account_confirmation')}
                        <br />
                        <span className={`font-mono ${isMobile ? 'text-xs' : 'text-sm'} bg-muted px-2 py-1 rounded mt-2 inline-block`}>
                          {tradingAccount.number_account_trading}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className={isMobile ? 'flex-col gap-2' : ''}>
                      <AlertDialogCancel className={isMobile ? 'w-full' : ''}>{t('cancel')}</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDelete}
                        className={`bg-destructive text-destructive-foreground hover:bg-destructive/90 ${isMobile ? 'w-full' : ''}`}
                      >
                        {t('delete')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Card>
    </Collapsible>
  );
}
